import { ChevronDown, CircleAlert } from 'lucide-react'
import { useId } from 'react'
import type { ComponentProps, ReactNode } from 'react'

import { ClearButton, useClearableInput } from '@/components/ui/clearable-input'
import { FormFieldContext, mergeDescribedBy, useFormControlProps } from '@/components/ui/form-field-context'
import { cn } from '@/lib/utils'

// 使い方のルール: docs/develop/client_ui/components/form.md

// 入力エラーの出し方:
// FormField / CompactFormField / InlineFormField に error（メッセージ）を渡すだけ。中の入力部品に
// aria-invalid（赤枠）と aria-describedby が自動で付き、入力欄の直下にアイコン付きのエラー文が出る。
// 入力側に aria-invalid を手書きしない。
// 検証のタイミング: 入力中は検証しない／保存ボタンを押したとき全項目を検証して focusFirstInvalidField で
// 最初のエラーへ移す／一度失敗した後は入力のたびに再検証して直した項目のエラーをその場で消す。
// エラーを理由に保存ボタンを disabled にしない（押せない理由が伝わらないため）。
// 項目のエラー文は role="alert" にしない: 保存押下時は最初のエラー項目へフォーカスが移り aria-describedby で読まれ、
// 入力ごとの再検証で alert が連発して読み上げが騒がしくなるため。
// フォーカスが動かない自動保存のセルだけ InlineFormField の announce でエラー文を alert にする

// このプロダクトのフォーム部品。ダイアログ・設定画面のフォームは全てここを使う。
//
// ui/input.tsx（shadcn の Input / Textarea）とは別物なので注意:
// あちらは h-8 の shadcn プリミティブで、フォームには使わない。
// 画面のフォームは v0 由来の h-11・角丸 xl の見た目で統一されているため、そちらをこのファイルに集約した
// （各ダイアログが同じ inputClass 文字列をコピーして持っていたのを1箇所にまとめたもの）。
//
// aria-invalid:* は shadcn の Input/Textarea（ui/input.tsx・ui/textarea.tsx）と同じ規約。
// リング幅は各コントロール自身の focus リング幅（通常フォームは ring-3、compact は ring-2）に揃える

export const formControlClass =
  'h-11 w-full rounded-xl border border-input bg-background px-3 text-sm font-normal outline-none placeholder:text-muted-foreground focus:border-primary focus:ring-3 focus:ring-ring/15 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20'

// 一覧の絞り込みと印刷設定で使う、情報密度を上げたフォーム部品。
// 通常フォームの h-11 / rounded-xl とは用途が異なるため、同じファイルで別系統として明示する。
export const compactFormControlClass =
  'h-9 w-full rounded-lg border border-input bg-card px-3 text-sm font-normal text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20'

/**
 * ラベルと入力欄の組。label で入力欄を包むので htmlFor / id は不要
 * （ラベルのクリックで入力欄にフォーカスが移る）。
 * エラー文・hint は label の外に出し、ラベルのアクセシブルネームに混ざらないようにしている
 */
export function FormField({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: ReactNode
  /** 入力欄の下に出す補足説明。入力の制約や正規化の説明など。エラーと併記される */
  hint?: ReactNode
  /** 値があるときだけ、入力欄を赤枠にして直下にエラー文を出す */
  error?: string
  children: ReactNode
  className?: string
}) {
  const baseId = useId()
  const errorId = `${baseId}-error`
  const hintId = `${baseId}-hint`
  const invalid = error !== undefined && error !== ''
  return (
    <FormFieldContext
      value={{
        controlId: undefined,
        invalid,
        describedBy: mergeDescribedBy(invalid ? errorId : undefined, hint === undefined ? undefined : hintId),
      }}
    >
      <div className={cn('flex flex-col gap-2', className)}>
        <label className="flex flex-col gap-2 text-sm font-bold">
          {label}
          {children}
        </label>
        {invalid && <FormFieldError id={errorId} message={error} />}
        {hint !== undefined && (
          <p id={hintId} className="text-xs leading-5 font-medium text-muted-foreground">
            {hint}
          </p>
        )}
      </div>
    </FormFieldContext>
  )
}

/** 入力欄の直下に出すエラー文。色だけに頼らないようアイコンを付ける。FormField 等が内部で使う */
export function FormFieldError({
  id,
  message,
  role,
  className,
}: {
  id?: string
  message: string
  role?: 'alert'
  className?: string
}) {
  return (
    <p id={id} role={role} className={cn('flex items-start gap-1 text-xs font-medium text-destructive', className)}>
      <CircleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
      {message}
    </p>
  )
}

/**
 * ラベルを持たない入力（表のセルの入力など、aria-label で名前を付けるもの）用のエラー表示枠。
 * 中の入力部品へエラー状態を配り、入力の直下にエラー文を出す
 */
export function InlineFormField({
  error,
  announce = false,
  children,
  className,
}: {
  error?: string
  /** true ならエラー文を role="alert" で読み上げさせる。フォーカスが動かない自動保存のセル用 */
  announce?: boolean
  children: ReactNode
  className?: string
}) {
  const errorId = `${useId()}-error`
  const invalid = error !== undefined && error !== ''
  return (
    <FormFieldContext value={{ controlId: undefined, invalid, describedBy: invalid ? errorId : undefined }}>
      <div className={cn('flex flex-col gap-1', className)}>
        {children}
        {invalid && <FormFieldError id={errorId} message={error} role={announce ? 'alert' : undefined} />}
      </div>
    </FormFieldContext>
  )
}

type ClearableInputProps = ComponentProps<'input'> & {
  /** Ant Design の allowClear 相当。値が入っているときだけ右端にクリアボタンを出す */
  clearable?: boolean
  /** クリアボタンの data-testid（未指定時は aria-label="クリア" で特定する） */
  clearButtonTestId?: string
}

export function FormInput({ className, clearable, clearButtonTestId, ...rest }: ClearableInputProps) {
  const props = { ...rest, ...useFormControlProps(rest) }
  if (clearable !== true) {
    // clearable でない呼び出し元（大多数）は useClearableInput 自体を呼ばない。
    // フックを常に呼ぶと「空↔非空」の遷移のたびに不要な state 更新が入ってしまうため、
    // clearable 専用の内側コンポーネントに閉じ込めて経路を分ける（下の ClearableFormInput 参照）
    return <input className={cn(formControlClass, className)} {...props} />
  }
  return <ClearableFormInput className={className} clearButtonTestId={clearButtonTestId} {...props} />
}

// clearable な入力欄は絶対配置のクリアボタン分の右パディングを常に確保し、
// 表示・非表示の切り替えで入力欄の幅・高さが変わらないようにする
function ClearableFormInput({ className, clearButtonTestId, ...props }: Omit<ClearableInputProps, 'clearable'>) {
  const { ref, hasValue, handleChange, handleClear } = useClearableInput(props)
  return (
    <div className="relative">
      {/* ref / onChange は props の展開より後ろに置く。props に呼び出し元の ref/onChange が
          含まれていても、こちらの合成 ref・ラップした onChange で上書きして両方を生かす */}
      <input className={cn(formControlClass, 'pr-9', className)} {...props} ref={ref} onChange={handleChange} />
      {hasValue && <ClearButton onClear={handleClear} testId={clearButtonTestId} />}
    </div>
  )
}

export function FormSelect({ className, disabled, ...rest }: ComponentProps<'select'>) {
  const props = { ...rest, ...useFormControlProps(rest) }
  return (
    <div className="relative">
      <select disabled={disabled} className={cn(formControlClass, 'appearance-none pr-9', className)} {...props} />
      <ChevronDown
        className={cn(
          'pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground',
          disabled === true && 'opacity-50',
        )}
      />
    </div>
  )
}

export function FormTextarea({ className, ...rest }: ComponentProps<'textarea'>) {
  const props = { ...rest, ...useFormControlProps(rest) }
  // 高さは rows に任せる（formControlClass の h-11 は1行入力用の固定高なので打ち消す）
  return <textarea className={cn(formControlClass, 'h-auto py-2', className)} {...props} />
}

/**
 * compact フォームのラベルと入力欄の組。label は id で入力欄と関連付ける。
 * id を省略すると自動で採番し、中の入力部品へ context で渡す。
 * 入力が複数あると全てに同じ id が付いて重複するので、その場合は各入力に id を明示する
 */
export function CompactFormField({
  id,
  label,
  error,
  children,
  className,
}: {
  id?: string
  label: ReactNode
  /** 値があるときだけ、入力欄を赤枠にして直下にエラー文を出す */
  error?: string
  children: ReactNode
  className?: string
}) {
  const generatedId = useId()
  const controlId = id ?? generatedId
  const errorId = `${controlId}-error`
  const invalid = error !== undefined && error !== ''
  return (
    <FormFieldContext value={{ controlId, invalid, describedBy: invalid ? errorId : undefined }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        <label htmlFor={controlId} className="text-xs font-medium text-muted-foreground">
          {label}
        </label>
        {children}
        {invalid && <FormFieldError id={errorId} message={error} />}
      </div>
    </FormFieldContext>
  )
}

export function CompactFormInput({ className, clearable, clearButtonTestId, ...rest }: ClearableInputProps) {
  const props = { ...rest, ...useFormControlProps(rest) }
  if (clearable !== true) {
    // 理由は FormInput のコメント参照（非 clearable 経路では useClearableInput を呼ばない）
    return <input className={cn(compactFormControlClass, className)} {...props} />
  }
  return <ClearableCompactFormInput className={className} clearButtonTestId={clearButtonTestId} {...props} />
}

function ClearableCompactFormInput({ className, clearButtonTestId, ...props }: Omit<ClearableInputProps, 'clearable'>) {
  const { ref, hasValue, handleChange, handleClear } = useClearableInput(props)
  return (
    <div className="relative">
      <input className={cn(compactFormControlClass, 'pr-8', className)} {...props} ref={ref} onChange={handleChange} />
      {hasValue && <ClearButton onClear={handleClear} testId={clearButtonTestId} />}
    </div>
  )
}

export function CompactFormSelect({ className, disabled, ...rest }: ComponentProps<'select'>) {
  const props = { ...rest, ...useFormControlProps(rest) }
  return (
    <div className="relative">
      <select
        disabled={disabled}
        className={cn(compactFormControlClass, 'appearance-none pr-9', className)}
        {...props}
      />
      <ChevronDown
        className={cn(
          'pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground',
          disabled === true && 'opacity-50',
        )}
      />
    </div>
  )
}

export function CompactFormTextarea({ className, ...rest }: ComponentProps<'textarea'>) {
  const props = { ...rest, ...useFormControlProps(rest) }
  return <textarea className={cn(compactFormControlClass, 'h-auto py-2', className)} {...props} />
}

/** 「SMSによるMFAログイン」のような、ラベル全体が枠になっているチェックボックス */
export function FormCheckboxField({
  label,
  className,
  ...props
}: ComponentProps<'input'> & {
  label: ReactNode
}) {
  const controlProps = { ...props, ...useFormControlProps(props) }
  return (
    <label className={cn('flex items-center gap-3 rounded-xl bg-secondary px-4 py-3 text-sm font-bold', className)}>
      <input
        type="checkbox"
        className="size-5 accent-primary aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20"
        {...controlProps}
      />
      {label}
    </label>
  )
}

/**
 * フォーム全体に関わるエラー（保存の失敗など、項目に結び付かないもの）の表示。
 * 項目の入力エラーは FormField の error で項目ごとに出す。
 * 保存の成否をユーザーに知らせる画面上部のバナーは ResultBanner（ui/result-banner.tsx）を使う
 */
export function FormErrorBanner({
  message,
  testId,
  className,
}: {
  message: string
  testId?: string
  className?: string
}) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn('mt-5 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm font-bold text-destructive', className)}
    >
      {message}
    </div>
  )
}

/**
 * 項目のエラーが画面外に出うる長いフォーム・表を含むフォームで、保存ボタンの近くに件数を知らせる要約。
 * 項目のエラー文の代わりにはならないので併用する。count が 0 なら何も描画しない
 */
export function FormErrorSummary({ count, testId, className }: { count: number; testId?: string; className?: string }) {
  if (count <= 0) {
    return null
  }
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        'flex items-start gap-2 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm font-bold text-destructive',
        className,
      )}
    >
      <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      入力内容に{count}件のエラーがあります。赤枠の項目を修正してください。
    </div>
  )
}

/**
 * container 内で最初にエラーになっている入力へフォーカスを移す。
 * エラー state を DOM に反映した後（useEffect / requestAnimationFrame）で呼ぶこと
 */
export function focusFirstInvalidField(container: HTMLElement | null) {
  const target = container?.querySelector<HTMLElement>('[aria-invalid="true"]')
  if (target === null || target === undefined) {
    return
  }
  target.focus({ preventScroll: true })
  target.scrollIntoView({ block: 'center' })
}
